import { Actions } from '../../lib/ActionSequence';
import StateObserver from '../../StateObserver';
import { openPopupPromise } from '../../lib/popups/popupOpenClose';
import platform from '@play-co/gcinstant';
import { Social } from '../../replicant/social';
import { shuffleArray } from 'src/replicant/utils/random';
import { isPlatformGuest, isPlatformLine } from '../../lib/utils';
import { getTutorialController } from '../tutorial/TutorialController';
import { SocialSubFeature } from 'src/replicant/social/social.ruleset';

type SocialFriendProfile = Pick<
  typeof platform['socialFriends'][number],
  'id' | 'name' | 'photo'
>;

let socialFriends: Promise<{
  affinity: SocialFriendProfile[];
  nonAffinity: SocialFriendProfile[];
}>;

async function getSocialFriendsList(): typeof socialFriends {
  if (!socialFriends) {
    socialFriends = Promise.resolve(
      isPlatformLine(platform)
        ? platform.getAffinitySortedFriendIdsAsync()
        : [],
    ).then((affinityFriends) => {
      const affinityFriendProfiles: SocialFriendProfile[] = [];
      const nonAffinityFriendProfiles: SocialFriendProfile[] = [];
      platform.socialFriends.forEach(({ id, name, photo }) => {
        if (!platform.friendIds.includes(id)) {
          const profile = { id, name, photo };
          const affinityIndex = affinityFriends.indexOf(id);
          if (affinityIndex >= 0) {
            affinityFriendProfiles[affinityIndex] = profile;
          } else {
            nonAffinityFriendProfiles.push(profile);
          }
        }
      });

      return {
        affinity: affinityFriendProfiles.filter((profile) => !!profile),
        nonAffinity: nonAffinityFriendProfiles,
      };
    });
  }

  return socialFriends;
}

export const socialSequences = {
  invite: (subFeature: SocialSubFeature) =>
    <Actions>[
      async function showGuestModePinch() {
        if (isPlatformGuest(platform)) {
          await openPopupPromise('popupNativeLineGuestLogin', {
            origin: 'sequence',
          });
          return true;
        }
        return false;
      },

      async function dispatchShareSequence() {
        await getTutorialController().handleEvent('friendReferralPopupOpened');
        return false;
      },

      async function inviteFriends() {
        const { affinity, nonAffinity } = await getSocialFriendsList();

        // Create dictionary of excluded friends for invite
        const { user } = StateObserver.getState();
        const { getExcludedFriendsForInvite } = Social.getters(user);
        const excludedFriends = getExcludedFriendsForInvite(
          [...affinity, ...nonAffinity].map(({ id }) => id),
          StateObserver.now(),
        ).reduce(
          (acc, id) => ({
            ...acc,
            [id]: true,
          }),
          {} as Record<string, boolean>,
        );

        // Filter and sort friends
        const filterExcludedFriend = ({ id }: SocialFriendProfile) =>
          !excludedFriends[id];
        const affinityFiltered = affinity.filter(filterExcludedFriend);
        const nonAffinityFiltered = nonAffinity.filter(filterExcludedFriend);
        shuffleArray(nonAffinityFiltered, Math.random);

        // Open invite popup with pre-processed friends list
        await openPopupPromise('popupInviteFriends', {
          friends: [...affinityFiltered, ...nonAffinityFiltered],
          subFeature,
        });
        return false;
      },
    ],

  /** Claim share rewards per player that joined the game through the share */
  referralRewards: (subFeature: SocialSubFeature) =>
    <Actions>[
      async function presentInviteRewardPopup() {
        const { user } = StateObserver.getState();
        await StateObserver.invoke.claimShareSuccessRewards(subFeature);
        await openPopupPromise('popupInviteReward', {
          inviteCount: Social.getters(user).getUnclaimedReferralRewardCount(),
          subFeature,
          titleKey: 'social.shareInvite.rewardSuccessTitle',
          subtitleKey: 'social.shareInvite.rewardSuccessSubtitle',
        });
        return false;
      },
    ],

  /** Present the share invite popup */
  shareInvite: (subFeature: string) =>
    <Actions>[
      async function presentInviteSharePopup() {
        await openPopupPromise('popupShareInvite', { subFeature });
        return false;
      },
    ],
};
